import api from '@/helpers/api'
import apiCalls from '@/helpers/apiCall'
import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

const TRUCKS_PER_SERVICE_CACHE_DURATION_MS = 300000
const TRUCKS_PER_SERVICE_CACHE_KEY = 'trucks:services:v1'

/** Evita GET /api/trucks/all em paralelo quando vários componentes chamam getVehicles ao mesmo tempo. */
let fetchVehiclesInFlight = null

/** Evita GET /api/trucks/services duplicados em paralelo. */
let fetchTrucksPerServiceInFlight = null

function readSessionCache(cacheKey) {
  try {
    const raw = sessionStorage.getItem(cacheKey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch (_) {
    return null
  }
}

function writeSessionCache(cacheKey, data) {
  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(data))
  } catch (_) {
    // Ignora erros de quota/serialização para não quebrar o fluxo principal.
  }
}

function removeSessionCache(cacheKey) {
  try {
    sessionStorage.removeItem(cacheKey)
  } catch (_) {
    // noop
  }
}

const state = {
  trucks: [],
  billingPeriodStore: [],
  trucksPerService: [],
  trucksPerServiceLastFetch: null
}
const mutations = {
  setTrucks(state, trucks) {
    // Garantir que sempre seja um array
    const trucksArray = Array.isArray(trucks)
      ? trucks
      : Array.isArray(trucks?.trucks)
      ? trucks.trucks
      : []
    state.trucks = trucksArray
  },
  getBillingPeriod(state, item) {
    state.billingPeriodStore.forEach(bp => {
      if (bp.subscriptionNumber == item.subscriptionNumber)
        bp.billingPeriod = item.billingPeriod
    })
    localStorage.setItem(
      'billingPeriod',
      JSON.stringify(state.billingPeriodStore)
    )
  },
  setBillingPeriod(state, billingPeriodStore) {
    state.billingPeriodStore = billingPeriodStore
  },
  setTrucksPerService(state, list) {
    state.trucksPerService = Array.isArray(list) ? list : []
    state.trucksPerServiceLastFetch = Date.now()
  },
  clearTrucksCache(state) {
    state.trucks = []
    state.trucksPerService = []
    state.trucksPerServiceLastFetch = null
  }
}

const actions = {
  /**
   * Sempre busca a lista de caminhões direto do backend (GET /api/trucks/all),
   * sem cache no frontend, para refletir imediatamente qualquer mudança
   * (ex.: novas assinaturas de visibilidade sincronizadas via Zuora).
   * Usado em portal-services e vehicles-overview. Mantém apenas a
   * deduplicação de chamadas em paralelo.
   */
  async getVehicles({ commit }) {
    if (fetchVehiclesInFlight) {
      return fetchVehiclesInFlight
    }

    const run = async () => {
      const res = await apiCalls.get(api.TrucksGetAll, '', '')
      const data = Array.isArray(res.data) ? res.data : []
      commit('setTrucks', data)
      return data
    }

    fetchVehiclesInFlight = run().finally(() => {
      fetchVehiclesInFlight = null
    })

    return fetchVehiclesInFlight
  },

  async getTrucksPerService({ commit, state }, forceRefresh = false) {
    const now = Date.now()

    if (forceRefresh) {
      removeSessionCache(TRUCKS_PER_SERVICE_CACHE_KEY)
    }

    const sessionCached = !forceRefresh
      ? readSessionCache(TRUCKS_PER_SERVICE_CACHE_KEY)
      : null
    if (
      sessionCached &&
      Array.isArray(sessionCached.data) &&
      typeof sessionCached.cachedAt === 'number' &&
      now - sessionCached.cachedAt < TRUCKS_PER_SERVICE_CACHE_DURATION_MS
    ) {
      commit('setTrucksPerService', sessionCached.data)
      return sessionCached.data
    }

    if (
      !forceRefresh &&
      state.trucksPerServiceLastFetch != null &&
      now - state.trucksPerServiceLastFetch < TRUCKS_PER_SERVICE_CACHE_DURATION_MS
    ) {
      return state.trucksPerService
    }

    if (!forceRefresh && fetchTrucksPerServiceInFlight) {
      return fetchTrucksPerServiceInFlight
    }

    const run = async () => {
      const res = await apiCalls.get(api.TrucksPerService, '', '')
      const data = Array.isArray(res.data) ? res.data : []
      commit('setTrucksPerService', data)
      writeSessionCache(TRUCKS_PER_SERVICE_CACHE_KEY, {
        cachedAt: Date.now(),
        data
      })
      return data
    }

    fetchTrucksPerServiceInFlight = run().finally(() => {
      fetchTrucksPerServiceInFlight = null
    })

    return fetchTrucksPerServiceInFlight
  },
  invalidateTrucksCache({ commit }) {
    removeSessionCache(TRUCKS_PER_SERVICE_CACHE_KEY)
    commit('clearTrucksCache')
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
